/**
 * Rules Catalog State (Reglas tab)
 *
 * Powers the scope-tree + inheritance-aware rules catalog. Holds the full
 * account rule set (including `kind: 'bypass'` rows needed for suppression
 * calculation), the sparse/lazy scope tree, and the master-detail UI state.
 *
 * The inheritance-annotated rule list for the selected scope is derived
 * (via `resolveRulesForScope`) — it is NOT stored here. See
 * `shared/utils/resolveRulesForScope.ts`.
 */

import { RulesReadmodelType } from '@bett3r-dev/teselly-domain';
import { ScopeNode } from '../../shared/utils/scopeTypes';

/**
 * Rules table filter (toolbar SegmentedControl).
 */
export type RulesFilter = 'all' | 'active' | 'inactive';

/**
 * Pending rule enable/disable request. Set by the user-sourced
 * `ui/requestRuleToggle` command (the submission trigger — user flips the row
 * Switch); cleared by the toggle policy once the `SetRule` + refetch round-trip
 * completes (or fails). `null` when no toggle is in flight.
 *
 * The CDSE policy engine reacts to STATE changes (not dispatched commands), so
 * this field is the trigger the `ruleTogglePolicy` watches via `shouldReact`.
 */
export type PendingToggle = {
  /** Readmodel rule id (`rule.id`) of the rule being toggled. */
  ruleId: string;
  /** Target enabled value to persist. */
  enabled: boolean;
} | null;

/**
 * Pending rule deletion request. Set by the user-sourced `ui/requestRuleDelete`
 * command (fired after the user confirms the delete modal); cleared by
 * `ruleDeletePolicy` once the `RemoveRule` + refetch round-trip completes (or
 * fails). `null` when no deletion is in flight.
 */
export type PendingDelete = {
  /** Readmodel rule id (`rule.id`) of the rule being deleted. */
  ruleId: string;
} | null;

/**
 * Main rules-catalog state.
 */
export type RulesCatalogState = {
  /** Core data slice. */
  data: {
    /**
     * All account rules INCLUDING `kind: 'bypass'` rows. Bypass rows are never
     * rendered as table entries but are required so `resolveRulesForScope` can
     * compute suppression correctly (the `/rules` route default-excludes them —
     * TV1-1648 — so they must be fetched with the explicit `kind` OR-clause).
     */
    rules: RulesReadmodelType[];

    /**
     * Built scope hierarchy (Cuenta -> Canales -> publicaciones; Cuenta ->
     * Ítems). The single synthetic root holds the rest of the tree as
     * `children`; `null` until the tree builder (ticket #8) populates it.
     */
    scopeTree: ScopeNode | null;

    /**
     * Entity ids pinned into the tree via lazy search (session-only — lost on
     * reload, accepted for v1).
     */
    pinnedScopeIds: string[];
  };

  /** Master-detail UI slice. */
  ui: {
    /** Selected scope node id (`'all'` resolves the same set as the account root). */
    selectedScopeId: string;

    /** Ids of rules whose inline Disparador -> Condición -> Acción detail is open. */
    expandedRuleIds: string[];

    /** Rules table filter (toolbar). */
    rulesFilter: RulesFilter;

    /** Rules table free-text search. */
    rulesSearch: string;

    /** Scope tree lazy entity search. */
    treeSearch: string;

    /**
     * In-flight rule enable/disable request, or `null`. Set on a user Switch
     * flip; consumed and cleared by `ruleTogglePolicy`. See {@link PendingToggle}.
     */
    pendingToggle: PendingToggle;

    /**
     * In-flight rule deletion request, or `null`. Set on confirmed delete;
     * consumed and cleared by `ruleDeletePolicy`. See {@link PendingDelete}.
     */
    pendingDelete: PendingDelete;
  };

  /** Loading slice (one flag per async concern). */
  loading: {
    /** Whether the account rule set is being fetched. */
    rulesLoading: boolean;

    /** Whether scope reference data (channels/pubs/items) is being fetched. */
    refDataLoading: boolean;
  };
};

export const initialState: RulesCatalogState = {
  data: {
    rules: [],
    scopeTree: null,
    pinnedScopeIds: []
  },
  ui: {
    selectedScopeId: 'all',
    expandedRuleIds: [],
    rulesFilter: 'all',
    rulesSearch: '',
    treeSearch: '',
    pendingToggle: null,
    pendingDelete: null
  },
  loading: {
    rulesLoading: false,
    refDataLoading: false
  }
};
